import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SeoArticleSchema from "@/components/SeoArticleSchema";
import { ContextualLinkPanel } from "@/components/ContextualLinkPanel";
import { InsightKeyTakeaway } from "@/components/InsightKeyTakeaway";

const sections = [
  {
    title: "Why can the headline number mislead?",
    paragraphs: [
      "There is no single answer that works across countries, cities, or lifestyles. A number that looks generous on a chart can feel tight once housing, healthcare, and the version of daily life you actually want are factored in.",
      "That is why cost ranges matter more than one tidy figure.",
    ],
  },
  {
    title: "What usually changes the real budget?",
    bullets: [
      "city choice and neighborhood pressure",
      "whether you expect public or private healthcare options",
      "household size and housing expectations",
      "the tradeoff between lower cost and weaker convenience or infrastructure",
    ],
  },
  {
    title: "What is the calmer budgeting question?",
    paragraphs: [
      "Instead of asking what the cheapest country is, ask what monthly budget would let this move feel stable and sustainable. That keeps the comparison grounded in reality rather than in best-case assumptions.",
    ],
  },
  {
    title: "What should a useful budget comparison actually tell you?",
    bullets: [
      "which countries clearly fit your range",
      "where the move still works but requires tighter tradeoffs",
      "which options start looking unrealistic without stretching too far",
    ],
  },
];

export default function HowMuchMoneyDoYouNeedToLiveAbroad() {
  return (
    <div className="min-h-screen bg-[#f7f4ee] text-stone-900" data-testid="how-much-money-live-abroad-page">
      <SeoArticleSchema
        headline="How Much Money Do You Need to Live Abroad?"
        description="Understand what realistic monthly budgets abroad actually depend on, and how to compare countries without relying on misleading averages."
        pathname="/insights/how-much-money-do-you-need-to-live-abroad"
        keywords={[
          "how much money do you need to live abroad",
          "budget to move abroad",
          "cost to live overseas",
        ]}
      />
      <Header />
      <main className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-6xl space-y-8">
          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-10 shadow-[0_18px_48px_rgba(28,25,23,0.06)] sm:px-10" data-testid="how-much-money-live-abroad-hero-section">
            <div className="mb-5 h-1.5 w-16 rounded-full bg-amber-400/80" />
            <p className="rc-section-kicker mb-4">Relocation insight</p>
            <h1 className="font-manrope text-4xl text-stone-900 sm:text-5xl">How Much Money Do You Need to Live Abroad?</h1>
            <div className="mt-6 max-w-4xl space-y-4 text-base leading-8 text-stone-600">
              <p>The workable monthly budget is rarely just a national average. Housing pressure, city choice, healthcare, and pace of life all change what realistic costs actually look like.</p>
              <p>A better budget comparison shows where the move fits comfortably, where it looks tighter, and where it stops making sense.</p>
            </div>
          </section>

          <InsightKeyTakeaway
            testIdPrefix="how-much-money-live-abroad-key-takeaway"
            paragraphs={[
              "There is no single amount of money that guarantees life abroad will work, because budgets change with city choice, housing expectations, healthcare use, and the kind of daily life you are trying to build.",
              "A more useful budget question is not simply whether a country looks cheap on paper, but whether your monthly range would still feel stable and sustainable once real tradeoffs show up.",
            ]}
          />

          <section className="grid gap-6 lg:grid-cols-2" data-testid="how-much-money-live-abroad-sections-grid">
            {sections.map((section, index) => (
              <article key={section.title} className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid={`how-much-money-live-abroad-section-${index + 1}`}>
                <h2 className="font-manrope text-2xl text-stone-900">{section.title}</h2>
                <div className="mt-5 space-y-4 text-base leading-8 text-stone-600">
                  {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                </div>
                {section.bullets?.length ? (
                  <ul className="mt-5 space-y-3 text-sm leading-7 text-stone-600">
                    {section.bullets.map((bullet) => (
                      <li key={bullet} className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-400/80" /><span>{bullet}</span></li>
                    ))}
                  </ul>
                ) : null}
              </article>
            ))}
          </section>

          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10" data-testid="how-much-money-live-abroad-followup-section">
            <h2 className="font-manrope text-2xl text-stone-900" data-testid="how-much-money-live-abroad-followup-title">What should you compare alongside budget?</h2>
            <div className="mt-5 max-w-4xl space-y-4 text-base leading-8 text-stone-600" data-testid="how-much-money-live-abroad-followup-copy">
              <p>
                Budget becomes more useful when it sits beside healthcare confidence, residency practicality, and the day-to-day shape of life abroad. That broader framing is why this question connects closely to{" "}
                <Link to="/insights/what-country-should-i-move-to" className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline" data-testid="how-much-money-live-abroad-what-country-link">
                  what actually determines country fit
                </Link>
                .
              </p>
              <p>
                If you are comparing retirement-friendly destinations specifically, it also helps to read{" "}
                <Link to="/insights/why-cost-to-retire-abroad-charts-are-misleading" className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline" data-testid="how-much-money-live-abroad-cost-chart-link">
                  why cost-to-retire-abroad charts do not tell the whole story
                </Link>
                , because that piece shows where general averages stop being enough to guide a real decision.
              </p>
            </div>
          </section>

          <ContextualLinkPanel
            kicker="Use the budget question well"
            title="These next pages keep budget connected to the full relocation decision."
            description="Budget becomes more useful when you pressure-test the charts, use it to narrow the shortlist, and then see how the paid Blueprint organizes affordability beside the rest of the tradeoffs."
            testIdPrefix="how-much-money-live-abroad-next-steps"
            items={[
              {
                slug: "cost-chart",
                eyebrow: "Pressure-test the numbers",
                title: "See why cost charts can mislead",
                description: "Go deeper on why one headline number rarely tells you whether the move really works.",
                ctaLabel: "Read the cost guide",
                to: "/insights/why-cost-to-retire-abroad-charts-are-misleading",
              },
              {
                slug: "shortlist",
                eyebrow: "Use budget to narrow",
                title: "Build a realistic shortlist",
                description: "Use affordability to remove weaker fits and keep only the countries worth deeper comparison.",
                ctaLabel: "Open the shortlist guide",
                to: "/insights/create-country-shortlist-for-moving-abroad",
              },
              {
                slug: "sample-blueprint",
                eyebrow: "See the paid layer",
                title: "Preview a Sample Blueprint",
                description: "See how cost, tradeoffs, and next steps are organized once the comparison becomes more personal.",
                ctaLabel: "Preview the sample",
                to: "/sample-plan",
              },
            ]}
          />

          <section className="rounded-[2rem] border border-stone-600/70 bg-stone-900 px-7 py-9 text-stone-50 shadow-[0_18px_48px_rgba(28,25,23,0.12)] sm:px-10" data-testid="how-much-money-live-abroad-cta-section">
            <div className="mb-5 h-1.5 w-14 rounded-full bg-amber-400/85" />
            <p className="text-sm font-medium uppercase tracking-[0.18em] text-stone-300">Next step</p>
            <h2 className="mt-3 font-manrope text-3xl text-stone-50">Want to pressure-test your budget before you commit to a country?</h2>
            <p className="mt-4 max-w-3xl text-base leading-8 text-stone-300">The assessment compares countries through realistic monthly fit, healthcare context, and the tradeoffs that show where your budget stretches cleanly — or starts to tighten.</p>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <Button asChild className="rounded-full bg-white px-6 text-stone-900 hover:bg-amber-100" data-testid="how-much-money-live-abroad-cta-button">
                <Link to="/auth">Start your assessment<ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}